import { useState, useEffect } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { GROUPS, SECTIONS } from "@/config/sections";

// Backoffice 왼쪽 고정 Sidebar (Phase 2-A, 2026-08-18).
// multi-expand: 각 그룹이 독립적으로 펼침/접힘. active 그룹은 항상 자동 펼침.
const EXPANDABLE = new Set(["dashboard", "admin"]);

export default function Sidebar() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab");

  // multi-expand 상태 — Set<groupKey>. active 그룹은 항상 포함.
  const [expanded, setExpanded] = useState(() => {
    const initial = new Set();
    for (const [key, group] of Object.entries(GROUPS)) {
      if (EXPANDABLE.has(key) && pathname.startsWith(group.path)) {
        initial.add(key);
      }
    }
    return initial;
  });

  // 라우트가 바뀌면 active 그룹을 자동으로 펼침 (접히지 않도록)
  useEffect(() => {
    for (const [key, group] of Object.entries(GROUPS)) {
      if (EXPANDABLE.has(key) && pathname.startsWith(group.path)) {
        setExpanded((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
      }
    }
  }, [pathname]);

  function toggleExpand(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <aside className="flex h-full w-56 shrink-0 flex-col border-r border-ink-100 bg-white">
      {/* 로고 */}
      <div className="flex h-14 items-center gap-2 border-b border-ink-100 px-4">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-500 text-[13px] font-bold text-white">N</span>
        <span className="text-[15px] font-semibold tracking-tight text-ink-900">
          Nova <span className="font-normal text-ink-400">Backoffice</span>
        </span>
      </div>

      {/* 메뉴 */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {Object.entries(GROUPS).map(([key, group]) => {
          const isGroupActive = pathname === group.path || pathname.startsWith(group.path + "/");
          const hasChildren = EXPANDABLE.has(key);
          const children = hasChildren ? SECTIONS.filter((s) => s.group === key) : [];
          const isExpanded = hasChildren && expanded.has(key);

          return (
            <div key={key} className="mb-1">
              {/* 상위 그룹 */}
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => toggleExpand(key)}
                  className={clsx(
                    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[14px] font-medium transition-colors",
                    isGroupActive
                      ? "bg-brand-50 text-brand-700"
                      : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                  )}
                >
                  {group.icon && <group.icon className="h-4 w-4 shrink-0" />}
                  <span className="flex-1 truncate">{group.label}</span>
                  <ChevronDown className={clsx(
                    "h-3.5 w-3.5 transition-transform",
                    isExpanded ? "rotate-180 text-brand-400" : "text-ink-300"
                  )} />
                </button>
              ) : (
                <NavLink
                  to={group.path}
                  className={() => clsx(
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-[14px] font-medium transition-colors",
                    isGroupActive
                      ? "bg-brand-50 text-brand-700"
                      : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                  )}
                >
                  {group.icon && <group.icon className="h-4 w-4 shrink-0" />}
                  <span className="flex-1 truncate">{group.label}</span>
                </NavLink>
              )}

              {/* 하위 탭 — 펼침 상태일 때 표시 */}
              {hasChildren && isExpanded && (
                <div className="ml-5 mt-0.5 space-y-0.5 border-l border-ink-100 pl-2">
                  {children.map((s, idx) => {
                    const isTabActive = isGroupActive &&
                      (currentTab === s.id || (!currentTab && idx === 0));

                    return (
                      <NavLink
                        key={s.id}
                        to={`${group.path}?tab=${s.id}`}
                        className={() => clsx(
                          "block rounded-md px-2.5 py-1.5 text-[13px] transition-colors",
                          isTabActive
                            ? "bg-brand-50 font-semibold text-brand-700"
                            : "text-ink-400 hover:bg-ink-50 hover:text-ink-700"
                        )}
                      >
                        {s.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
